import { useState, useEffect } from 'react';
import './App.css';
import { useTelegram } from './hooks/useTelegram';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import HomePage from './components/HomePage/HomePage';
import RewardsPage from './components/RewardsPage/RewardsPage';
import WalletsPage from './components/WalletsPage/WalletsPage';
import { userStatistics } from './utils/api';
import { useUI } from './hooks/useUI';
import { preloadResources } from './utils/resources';
import CardPage from './components/CardPage/CardPage';
import BuybackPage from './components/BuybackPage/BuybackPage';

function App() {

    const { tg } = useTelegram();
    const { setUserData, userData = {}, startNewTimer } = useUI();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isResourcesLoaded, setIsResourcesLoaded] = useState(false);
    const [showPreloader, setShowPreloader] = useState(true);
    const location = useLocation();
    const navigate = useNavigate();

    const preloadImages = async (urls) => {
        const preloadContainer = document.createElement('div');
        preloadContainer.style.display = 'none';
        document.body.appendChild(preloadContainer);

        const promises = urls.map((url) => {
            return new Promise((resolve, reject) => {
                const link = document.createElement('link');
                link.rel = 'preload';
                link.as = 'image';
                link.href = url;
                link.onload = () => resolve();
                link.onerror = () => reject(new Error(`Failed to preload image: ${url}`));
                document.head.appendChild(link);

                const img = new Image();
                img.src = url;
                img.onload = () => {
                    console.log(`Loaded: ${url}`);
                    resolve();
                };
                img.onerror = (event) => {
                    console.error(`Failed to load: ${url}`, event);
                    reject(new Error(`Failed to load image: ${url}`));
                };
                preloadContainer.appendChild(img);
            });
        });

        try {
            await Promise.all(promises);
        } finally {
            document.body.removeChild(preloadContainer);
        }
    };

    useEffect(() => {
        const initializeApp = async () => {
            try {
                tg.ready();
                tg.expand();
                if (!window.Telegram?.WebApp?.initData) {
                    throw new Error('Telegram WebApp not initialized');
                }
                const [response] = await Promise.all([
                    userStatistics(window.Telegram.WebApp.initData),
                    preloadImages(preloadResources),
                ]);

                console.log(response.result);

                setUser(response.result);
                setUserData(response.result);
                
                setIsResourcesLoaded(true);
            } catch (err) {
                console.error('Initialization error:', err);
                setError(err.message || 'Failed to load user data or resources');
            } finally {
                setLoading(false);
            }
        };
        initializeApp();
    }, [tg, setUserData]);

    useEffect(() => {
        if (!loading && isResourcesLoaded && !showPreloader) {
            if (userData?.user_wallets?.length < 1 && location.pathname !== '/wallets') {
                navigate('/wallets');
            }
        }
    }, [loading, isResourcesLoaded, showPreloader, userData, location.pathname, navigate]);

    useEffect(() => {
        if (!loading && isResourcesLoaded) {
            const timer = setTimeout(() => {
                setShowPreloader(false);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [loading, isResourcesLoaded]);

    if (showPreloader) {
        return (
            <div
                className="loading-bar"
                style={{
                    opacity: !loading && isResourcesLoaded ? 0 : 1,
                    transition: 'opacity 0.5s ease-in-out',
                }}
            >
                <div className="loading-bar__progress"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="App" style={{ opacity: 1, transition: 'opacity 1.2s ease-in-out' }}>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="App" style={{ opacity: 1 }}>
            <Routes location={location} key={location.pathname}>
                <Route
                    index
                    element={
                        <div className="page-transition">
                            <HomePage user={user} mainBalance={userData.user_main_balance} usdBalance={userData.user_usd_balance} />
                        </div>
                    }
                />
                <Route
                    path="rewards"
                    element={
                        <div className="page-transition">
                            <RewardsPage />
                        </div>
                    }
                />
                <Route
                    path="wallets"
                    element={
                        <div className="page-transition">
                            <WalletsPage user={userData} />
                        </div>
                    }
                />
                <Route
                    path="card"
                    element={
                        <div className="page-transition">
                            <CardPage user={userData} />
                        </div>
                    }
                />
                <Route
                    path="buyback"
                    element={
                        <div className="page-transition">
                            <BuybackPage user={userData} />
                        </div>
                    }
                />
            </Routes>
        </div>
    );
}

export default App;